import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import BookingChat from '../components/BookingChat';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, ClockCounterClockwise, Star, ChatCircleText } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { toast } from 'sonner';

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chatBookingId, setChatBookingId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, requestsRes, analyticsRes] = await Promise.all([
        api.get('/api/bookings'),
        api.get('/api/requests'),
        api.get('/api/analytics/user'),
      ]);
      setBookings(bookingsRes.data);
      setRequests(requestsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-sky-100 text-sky-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-background">

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2" data-testid="dashboard-title">
            Ciao, {user?.name}!
          </h1>
          <p className="text-foreground/70">Gestisci le tue prenotazioni e richieste</p>
        </div>

        <div className="mb-6">
          <EmailVerificationBanner />
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <Button className="rounded-full" onClick={() => navigate('/dashboard/nuova-richiesta')} data-testid="cta-new-request">
            + Nuova richiesta
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/dashboard/richieste')} data-testid="cta-my-requests">
            Le mie richieste
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/dashboard/cartella')} data-testid="cta-patient-record">
            La mia cartella
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="neumorphism rounded-2xl p-6" data-testid="stats-bookings">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Calendar size={24} className="text-primary" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{bookings.length}</p>
                <p className="text-sm text-foreground/70">Prenotazioni</p>
              </div>
            </div>
          </Card>

          <Card className="neumorphism rounded-2xl p-6" data-testid="stats-requests">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                <ClockCounterClockwise size={24} className="text-accent" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{requests.length}</p>
                <p className="text-sm text-foreground/70">Richieste</p>
              </div>
            </div>
          </Card>

          <Card className="neumorphism rounded-2xl p-6" data-testid="stats-reviews">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                <Star size={24} className="text-accent" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{analytics?.reviews_count ?? 0}</p>
                <p className="text-sm text-foreground/70">Recensioni Scritte</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Analytics cliente */}
        {analytics && (
          <div className="grid md:grid-cols-3 gap-6 mb-8" data-testid="client-analytics">
            {/* Monthly spend */}
            <Card className="neumorphism rounded-2xl p-6" data-testid="analytics-monthly-spend">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Spesa mensile</h3>
                <span className="text-xs text-foreground/60">Ultimi 6 mesi</span>
              </div>
              <div className="text-3xl font-heading font-bold text-primary mb-1">€{Number(analytics.total_spent || 0).toFixed(2)}</div>
              <div className="text-xs text-foreground/60 mb-4">totale storico</div>
              <div className="space-y-1">
                {(analytics.monthly_spend || []).length === 0 ? (
                  <p className="text-xs text-foreground/60">Nessuna spesa registrata ancora.</p>
                ) : (
                  (() => {
                    const max = Math.max(...analytics.monthly_spend.map((m) => m.amount), 1);
                    return analytics.monthly_spend.map((m) => (
                      <div key={m.month} className="flex items-center gap-2 text-xs">
                        <span className="w-14 text-foreground/60">{m.month}</span>
                        <div className="flex-1 h-2 rounded-full bg-muted">
                          <div className="h-2 rounded-full bg-primary" style={{ width: `${(m.amount / max) * 100}%` }} />
                        </div>
                        <span className="w-16 text-right font-medium">€{m.amount.toFixed(0)}</span>
                      </div>
                    ));
                  })()
                )}
              </div>
            </Card>

            {/* Favorite pros */}
            <Card className="neumorphism rounded-2xl p-6" data-testid="analytics-favorites">
              <h3 className="font-semibold text-lg mb-4">I tuoi preferiti</h3>
              {(analytics.favorite_professionals || []).length === 0 ? (
                <p className="text-xs text-foreground/60">Prenota un professionista per vederlo qui.</p>
              ) : (
                <div className="space-y-3">
                  {analytics.favorite_professionals.map((p) => (
                    <div key={p.professional_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/professional/${p.professional_id}`)} data-testid={`fav-pro-${p.professional_id}`}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {p.picture ? <img src={p.picture} alt="" className="w-full h-full object-cover" /> : <Star size={16} weight="fill" className="text-primary" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.name}</div>
                        <div className="text-xs text-foreground/60 truncate">
                          {p.location || p.profession_code} • {p.bookings_with_you}× prenotato
                        </div>
                      </div>
                      <div className="text-xs text-yellow-600 flex items-center gap-0.5">
                        <Star size={10} weight="fill" /> {(p.rating || 0).toFixed(1)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Upcoming visits — 5 accettate + 10 in corso */}
            <Card className="neumorphism rounded-2xl p-6" data-testid="analytics-upcoming">
              <h3 className="font-semibold text-lg mb-4">Prossime visite</h3>
              {((analytics.upcoming_accepted || []).length === 0 && (analytics.upcoming_in_progress || []).length === 0) ? (
                <p className="text-xs text-foreground/60">Nessuna visita programmata.</p>
              ) : (
                <div className="space-y-4">
                  {(analytics.upcoming_accepted || []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-indigo-700 mb-2 uppercase tracking-wide">Accettate ({analytics.upcoming_accepted.length}/5)</div>
                      <div className="space-y-2">
                        {analytics.upcoming_accepted.map((v) => (
                          <div key={v.request_id} className="p-3 rounded-lg border border-border" data-testid={`upcoming-accepted-${v.request_id}`}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <Badge className="bg-indigo-100 text-indigo-800">Accettata</Badge>
                              {v.is_urgent && <Badge className="bg-red-100 text-red-800 text-[10px]">URGENTE</Badge>}
                              <span className="text-foreground/60">{v.start_date ? new Date(v.start_date).toLocaleDateString('it-IT') : '—'}</span>
                            </div>
                            <div className="text-sm font-medium truncate">{v.service_type || v.patient_name || 'Richiesta'}</div>
                            {v.region && <div className="text-xs text-foreground/60 truncate">{v.region}{v.comune ? ` · ${v.comune}` : ''}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(analytics.upcoming_in_progress || []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">In corso ({analytics.upcoming_in_progress.length}/10)</div>
                      <div className="space-y-2">
                        {analytics.upcoming_in_progress.map((v) => (
                          <div key={v.request_id} className="p-3 rounded-lg border border-border" data-testid={`upcoming-inprogress-${v.request_id}`}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <Badge className="bg-amber-100 text-amber-800">In corso</Badge>
                              {v.is_urgent && <Badge className="bg-red-100 text-red-800 text-[10px]">URGENTE</Badge>}
                              <span className="text-foreground/60">{v.start_date ? new Date(v.start_date).toLocaleDateString('it-IT') : '—'}</span>
                            </div>
                            <div className="text-sm font-medium truncate">{v.service_type || v.patient_name || 'Richiesta'}</div>
                            {v.region && <div className="text-xs text-foreground/60 truncate">{v.region}{v.comune ? ` · ${v.comune}` : ''}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        <Card className="neumorphism rounded-2xl p-6">
          <Tabs defaultValue="bookings">
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="bookings">Prenotazioni</TabsTrigger>
              <TabsTrigger value="requests">Richieste</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-foreground/70">Nessuna prenotazione ancora</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="bookings-list">
                  {bookings.map((booking) => {
                    const canReview = (booking.status === 'confirmed' || booking.status === 'completed') && booking.payment_status === 'paid';
                    return (
                    <div
                      key={booking.booking_id}
                      className="border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`booking-${booking.booking_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-lg mb-2">{booking.service_type}</p>
                          <p className="text-sm text-foreground/70 mb-1">
                            Data: {new Date(booking.date).toLocaleDateString('it-IT')} - {booking.time}
                          </p>
                          <p className="text-sm text-foreground/70">Importo: {booking.amount}€</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={getStatusColor(booking.status)}>
                            {booking.status}
                          </Badge>
                          <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => setChatBookingId(booking.booking_id)} data-testid={`open-chat-${booking.booking_id}`}>
                            <ChatCircleText size={14} className="mr-1" /> Chat
                          </Button>
                          {canReview && !booking.has_review && (
                            <Button size="sm" className="rounded-full h-7 bg-yellow-500 hover:bg-yellow-600" onClick={() => setReviewFor(booking)} data-testid={`review-booking-${booking.booking_id}`}>
                              <Star size={12} weight="fill" className="mr-1" /> Recensione verificata
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="requests" className="mt-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-foreground/70">Nessuna richiesta ancora</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="requests-list">
                  {requests.map((request) => (
                    <div
                      key={request.request_id}
                      className="border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`request-${request.request_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-lg mb-2">{request.service_type}</p>
                          <p className="text-sm text-foreground/70 mb-2">{request.description}</p>
                          <div className="flex gap-4 text-sm text-foreground/60">
                            <span>Località: {request.location}</span>
                            <span>Urgenza: {request.urgency}</span>
                          </div>
                        </div>
                        <Badge className={getStatusColor(request.status)}>
                          {request.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      {chatBookingId && (
        <BookingChat bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
      )}
      {/* Review dialog for verified reviews on paid+confirmed/completed bookings */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => { if (!o) setReviewFor(null); }}>
        <DialogContent data-testid="verified-review-dialog">
          <DialogHeader>
            <DialogTitle>Recensione verificata</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-foreground/70">La tua recensione è verificata perché deriva da una prenotazione completata e pagata. Aiuta gli altri clienti e fai salire i migliori professionisti nel matching.</p>
            <div>
              <Label className="mb-2 block">Valutazione</Label>
              <div className="flex gap-1" data-testid="review-stars-row">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setReviewRating(n)} className="transition-transform hover:scale-110" data-testid={`review-star-${n}`}>
                    <Star size={32} weight={n <= reviewRating ? 'fill' : 'regular'} className={n <= reviewRating ? 'text-yellow-500' : 'text-foreground/30'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Commento (opzionale)</Label>
              <Textarea rows={4} value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Puntualità, competenza, empatia…" data-testid="review-comment-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewFor(null)}>Annulla</Button>
            <Button
              disabled={savingReview}
              onClick={async () => {
                if (!reviewFor) return;
                setSavingReview(true);
                try {
                  await api.post('/api/reviews', {
                    booking_id: reviewFor.booking_id,
                    professional_id: reviewFor.professional_id,
                    rating: reviewRating,
                    comment: reviewComment,
                  });
                  toast.success('Recensione verificata pubblicata — grazie!');
                  setReviewFor(null); setReviewComment(''); setReviewRating(5);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Errore recensione');
                } finally { setSavingReview(false); }
              }}
              data-testid="submit-verified-review-btn"
            >
              {savingReview ? 'Invio…' : 'Pubblica recensione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;